import { isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import Modal from 'react-modal';

import Logo from '../../../assets/logos/Ark51_HORIWHITE&GRAD.png';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { systemMaintenanceOptions } from '../../admin/system-maintenance/store';
import { ClientFeaturePermission, FeaturePermission } from '../../admin/users/store';
import { getClientHasFeaturePermission, getUser, getUserHasOneFeaturePermissionNoAdmin } from '../../auth/login/store';
import { ArkModule, setUnauthorisedModule, toggleUserNotPermittedModalOpen } from '../../home/store';
import { OpinionsView } from '../../opinions/my-opinions/store';
import { Icon as ArkIcon } from '../../shared/icon/Icon';
import { Attestation, Bookshelf, Briefcase, Document, Engine, Export, HamburgerMenu, Home, MultiAnalytics, Opinion, OpinionAnalytics, OpinionReporting, PlaybookIcon, Search, SupplyChain, ThirdParty } from '../../shared/icons';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import styles from '../HomeWrapper.module.scss';
import { getLeftMenuOpen, toggleLeftMenu } from '../store';
import { MenuTileId, MenuTileProps, TilesMenu } from './MenuTile';
import { NavigationPath, Path } from './NavigationPath';

export const LeftMenu: React.FC = () => {
    const [selectedTile, setSelectedTile] = useState<MenuTileId | null>(null);

    const isOpen = useAppSelector(getLeftMenuOpen);
    const user = useAppSelector(getUser);
    const dispatch = useAppDispatch();
    const hasDocumentModulePermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.DOCUMENT_MODULE]));
    const hasOpinionModulePermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.OPINION_MODULE]));
    const hasDoraModulePermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.DORA_MODULE]));
    const userHasDoraFeaturePermissions = useAppSelector(getUserHasOneFeaturePermissionNoAdmin([FeaturePermission.DORA_FULL_ACCESS, FeaturePermission.DORA_READ_ONLY]));

    const closeMenu = useCallback(() => {
        dispatch(toggleLeftMenu(false));
        setSelectedTile(null);
    }, [dispatch]);

    const openUserNotPermittedModule = useCallback(() => dispatch(toggleUserNotPermittedModalOpen(true)), [dispatch]);

    const openUnauthorisedModule = useCallback((module: ArkModule) => dispatch(setUnauthorisedModule(module)), [dispatch]);

    const handleDoraModuleClick = useCallback(() => userHasDoraFeaturePermissions ? closeMenu() : openUserNotPermittedModule(), [userHasDoraFeaturePermissions, closeMenu, openUserNotPermittedModule]);
    const handleDocumentModuleClick = useCallback(() => hasDocumentModulePermission ? closeMenu() : openUnauthorisedModule(ArkModule.DOCUMENT), [hasDocumentModulePermission, closeMenu, openUnauthorisedModule]);
    const handleOpinionModuleClick = useCallback(() => hasOpinionModulePermission ? closeMenu() : openUnauthorisedModule(ArkModule.OPINION), [hasOpinionModulePermission, closeMenu, openUnauthorisedModule]);

    const defaultOpinionView = useMemo(() => user?.userDefaults.opinionPageView === OpinionsView.TABLE ? 'table' : 'map', [user]);

    const systemMaintenanceIds = useMemo(() => user && user.systemMaintenanceIds || [], [user]);

    const menuTileIdsDisabledForMaintenance = useMemo(() => systemMaintenanceIds.reduce((acc: string[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].menuTileId)) {
            acc.push(systemMaintenanceOptions[cur].menuTileId!);
        }
        return acc;
    }, []), [systemMaintenanceIds]);

    const disabledTileMaintenanceTooltip = useMemo(() => systemMaintenanceIds.reduce((acc: { menuTileId: string, tooltip: string }[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].menuTileId)) {
            acc.push({ menuTileId: systemMaintenanceOptions[cur].menuTileId!, tooltip: systemMaintenanceOptions[cur].tooltipMessage });
        }
        return acc;
    }, []), [systemMaintenanceIds]);

    const handleTileClick = useCallback((tileId: MenuTileId | null) => {
        if (isNull(tileId)) {
            setSelectedTile(tileId);
        }
        if (!isNull(tileId) && menuTileIdsDisabledForMaintenance.includes(tileId)) {
            return noop;
        }
        switch (tileId) {
            case MenuTileId.USER_DOCUMENTS_MODULE:
            case MenuTileId.USER_OPINIONS_MODULE:
            case MenuTileId.USER_DORA_MODULE:
                return setSelectedTile(tileId);
            case MenuTileId.USER_MY_DOCUMENTS:
            case MenuTileId.USER_DOCUMENT_SEARCH:
            case MenuTileId.USER_DOCUMENT_REPORTING:
            case MenuTileId.USER_DOCUMENT_ANALYTICS:
                return handleDocumentModuleClick();
            case MenuTileId.USER_MY_OPINIONS:
            case MenuTileId.USER_OPINION_REPORTING:
            case MenuTileId.USER_OPINION_ANALYTICS:
            case MenuTileId.USER_OPINION_NETTING_ENGINE:
                return handleOpinionModuleClick();
            case MenuTileId.USER_DORA_DATA_MANAGEMENT:
            case MenuTileId.USER_DORA_ANALYTICS:
            case MenuTileId.USER_DORA_REPORTING:
                return handleDoraModuleClick();
            default:
                return closeMenu();
        }
    }, [closeMenu, handleDocumentModuleClick, handleOpinionModuleClick, handleDoraModuleClick, menuTileIdsDisabledForMaintenance]);

    const checkTileDisabled = useCallback((menuTileId: MenuTileId, requiredPermission: boolean | undefined = undefined) => {
        if (!isUndefined(requiredPermission) && !requiredPermission) {
            return true;
        }
        return menuTileIdsDisabledForMaintenance.includes(menuTileId);
    }, [menuTileIdsDisabledForMaintenance]);

    const getDisabledTileTooltip = useCallback((tile: MenuTileId) => disabledTileMaintenanceTooltip.find(({ menuTileId }) => menuTileId === tile)?.tooltip, [disabledTileMaintenanceTooltip]);

    const allChildTiles: MenuTileProps[] = useMemo(() => ([
        {
            id: MenuTileId.USER_MY_DOCUMENTS,
            label: 'My Documents',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_MY_DOCUMENTS, hasDocumentModulePermission),
            icon: Document,
            path: '/documents/my-documents',
            parent: MenuTileId.USER_DOCUMENTS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MY_DOCUMENTS)
        },
        {
            id: MenuTileId.USER_DOCUMENT_SEARCH,
            label: 'Search',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DOCUMENT_SEARCH, hasDocumentModulePermission),
            icon: Search,
            path: '/documents/search',
            parent: MenuTileId.USER_DOCUMENTS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DOCUMENT_SEARCH)
        },
        {
            id: MenuTileId.USER_DOCUMENT_REPORTING,
            label: 'Reporting',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DOCUMENT_REPORTING, hasDocumentModulePermission),
            icon: Export,
            path: '/documents/reporting',
            parent: MenuTileId.USER_DOCUMENTS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DOCUMENT_REPORTING)
        },
        {
            id: MenuTileId.USER_DOCUMENT_ANALYTICS,
            label: 'Analytics',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DOCUMENT_ANALYTICS, hasDocumentModulePermission),
            icon: MultiAnalytics,
            path: '/documents/analytics',
            parent: MenuTileId.USER_DOCUMENTS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DOCUMENT_ANALYTICS)
        },
        {
            id: MenuTileId.USER_MY_OPINIONS,
            label: 'My Opinions',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_MY_OPINIONS, hasOpinionModulePermission),
            icon: Opinion,
            path: `/opinions/my-opinions/${defaultOpinionView}`,
            parent: MenuTileId.USER_OPINIONS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_MY_OPINIONS)
        },
        {
            id: MenuTileId.USER_OPINION_REPORTING,
            label: 'Reporting',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_OPINION_REPORTING, hasOpinionModulePermission),
            icon: OpinionReporting,
            path: '/opinions/reporting',
            parent: MenuTileId.USER_OPINIONS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_OPINION_REPORTING)
        },
        {
            id: MenuTileId.USER_OPINION_ANALYTICS,
            label: 'Analytics',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_OPINION_ANALYTICS, hasOpinionModulePermission),
            icon: OpinionAnalytics,
            path: '/opinions/analytics',
            parent: MenuTileId.USER_OPINIONS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_OPINION_ANALYTICS)
        },
        {
            id: MenuTileId.USER_OPINION_NETTING_ENGINE,
            label: 'Netting Engine',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_OPINION_NETTING_ENGINE, hasOpinionModulePermission),
            icon: Engine,
            path: '/opinions/netting-engine',
            parent: MenuTileId.USER_OPINIONS_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_OPINION_NETTING_ENGINE)
        },
        {
            id: MenuTileId.USER_DORA_DATA_MANAGEMENT,
            label: 'Data Management',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DORA_DATA_MANAGEMENT, hasDoraModulePermission),
            icon: SupplyChain,
            path: '/dora/data-management',
            parent: MenuTileId.USER_DORA_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DORA_DATA_MANAGEMENT)
        },
        {
            id: MenuTileId.USER_DORA_REPORTING,
            label: 'Reporting',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DORA_REPORTING, hasDoraModulePermission),
            icon: Export,
            path: '/dora/reporting',
            parent: MenuTileId.USER_DORA_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DORA_REPORTING)
        },
        {
            id: MenuTileId.USER_DORA_ANALYTICS,
            label: 'Analytics',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DORA_ANALYTICS, hasDoraModulePermission),
            icon: MultiAnalytics,
            path: '/dora/analytics',
            parent: MenuTileId.USER_DORA_MODULE,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DORA_ANALYTICS)
        }
    ]), [handleTileClick, checkTileDisabled, hasDocumentModulePermission, getDisabledTileTooltip, hasOpinionModulePermission, defaultOpinionView, hasDoraModulePermission]);

    const childTiles = useMemo(() => allChildTiles.filter(({ parent }) => parent === selectedTile), [selectedTile, allChildTiles]);

    const groupTiles: MenuTileProps[] = useMemo(() => ([
        {
            id: MenuTileId.HOME,
            label: 'Home',
            onClick: handleTileClick,
            icon: Home,
            path: '/home'
        },
        {
            id: MenuTileId.USER_DOCUMENTS_MODULE,
            label: 'Documents',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DOCUMENTS_MODULE, hasDocumentModulePermission),
            icon: Document,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DOCUMENTS_MODULE)
        },
        {
            id: MenuTileId.USER_OPINIONS_MODULE,
            label: 'Opinions',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_OPINIONS_MODULE, hasOpinionModulePermission),
            icon: Opinion,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_OPINIONS_MODULE)
        },
        {
            id: MenuTileId.USER_PLAYBOOKS,
            label: 'Playbooks',
            onClick: handleTileClick,
            icon: PlaybookIcon,
            path: '/playbook',
            disabled: checkTileDisabled(MenuTileId.USER_PLAYBOOKS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_PLAYBOOKS)
        },
        {
            id: MenuTileId.USER_ATTESTATIONS,
            label: 'My Attestations',
            onClick: handleTileClick,
            icon: Attestation,
            path: '/my-attestations',
            disabled: checkTileDisabled(MenuTileId.USER_ATTESTATIONS),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_ATTESTATIONS)
        },
        {
            id: MenuTileId.USER_CLAUSE_LIBRARY,
            label: 'Clause Library',
            onClick: handleTileClick,
            icon: Bookshelf,
            path: '/clause-library',
            disabled: checkTileDisabled(MenuTileId.USER_CLAUSE_LIBRARY),
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_CLAUSE_LIBRARY)
        },
        {
            id: MenuTileId.USER_DORA_MODULE,
            label: 'DORA',
            onClick: handleTileClick,
            disabled: checkTileDisabled(MenuTileId.USER_DORA_MODULE, hasDoraModulePermission),
            icon: ThirdParty,
            disabledTooltip: getDisabledTileTooltip(MenuTileId.USER_DORA_MODULE)
        },
        {
            id: MenuTileId.USER_NEGOTIATION_GUIDES,
            label: 'Negotiation Guides',
            onClick: handleTileClick,
            icon: Briefcase,
            path: '/guides'
        }
    ]), [handleTileClick, checkTileDisabled, hasDocumentModulePermission, getDisabledTileTooltip, hasOpinionModulePermission, hasDoraModulePermission]);

    const allTiles = useMemo(() => [...allChildTiles, ...groupTiles], [allChildTiles, groupTiles]);

    const scrollableHeight = useMemo(() => `calc(100% - ${isNull(selectedTile) ? 1 : 31}px)`, [selectedTile]);

    const getPath = useCallback((tileId: MenuTileId): Path[] => {
        let menuPath: Path = { id: null, label: 'Menu' };
        switch (tileId) {
            case MenuTileId.USER_DOCUMENTS_MODULE:
                return [menuPath, { id: MenuTileId.USER_DOCUMENTS_MODULE, label: 'Documents' }];
            case MenuTileId.USER_OPINIONS_MODULE:
                return [menuPath, { id: MenuTileId.USER_OPINIONS_MODULE, label: 'Opinions' }];
            case MenuTileId.USER_DORA_MODULE:
                return [menuPath, { id: MenuTileId.USER_DORA_MODULE, label: 'DORA' }];
            default:
                return [menuPath];
        }
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.leftMenu}
            ariaHideApp={false}
            style={{ overlay: { display: 'flex', zIndex: 10 } }}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
            onRequestClose={closeMenu}
        >
            <div data-testid='left-menu-wrapper' className={styles.leftMenuWrapper}>
                <div className={styles.leftMenuButtonWrapper}>
                    <div className={styles.logoWrapper}>
                        <img className={styles.logo} src={Logo} />
                    </div>
                    <button data-testid='left-menu-button' className={styles.leftMenuButton} onClick={closeMenu}>
                        <ArkIcon icon={HamburgerMenu} fontSize={40} />
                    </button>
                </div>
                <div className={styles.menuWrapper}>
                    <NavigationPath getPath={getPath} selectPath={setSelectedTile} selectedTile={selectedTile} />
                    <div className={styles.scrollableWrapper} style={{ height: scrollableHeight }}>
                        <Scrollable>
                            <TilesMenu tiles={groupTiles} selected={selectedTile} childTiles={childTiles} allTiles={allTiles} />
                        </Scrollable>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
